// Dip PDP — below-the-fold interactivity (deferred; separate from the buy-critical
// dip-buy-box.js on purpose). Three small pieces:
//   <dip-review-wall>  topic filter for the testimonial wall (no-JS: all visible, bar hidden)
//   <dip-video-card>   poster -> inline video swap; only one card plays at a time
//   count-up           [data-countup] numbers animate up on first view
//   reveal-on-scroll   the shared .dip-reveal observer (see the bottom of this file)

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

// ---- Filtered review wall ---------------------------------------------------
// One component, two filter flavours: topic chips (data-topic, testimonial wall)
// and star-rating chips (data-stars, the Trustpilot reviews feed). Filtered views
// stay capped (cap-desktop / cap-mobile) with a Show-more button that reveals the
// rest of the current view; changing filter re-caps. No-JS: bar hidden, all visible.
class DipReviewWall extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-filter-bar]');
    this.sourceBar = this.querySelector('[data-source-bar]');
    this.topicBar = this.querySelector('[data-topic-bar]');
    this.productBar = this.querySelector('[data-product-bar]');
    this.status = this.querySelector('[data-filter-status]');
    this.moreBtn = this.querySelector('[data-show-more]');
    this.cards = [...this.querySelectorAll('[data-topic], [data-stars]')].filter((el) => !el.matches('button'));
    this.wallCards = this.cards.filter((el) => el.matches('.dip-rw-card'));
    if (!this.cards.length) return;
    this.extra = 0; // pages revealed by Show-more (row-filled increments)
    // Three independent dimensions (2026-08-24, the Simple arm's review hub): star rating,
    // topic, and platform. Legacy walls carry one bar and simply never move the other two.
    // With a source bar present the platforms stay SEPARATE pools: Trustpilot opens.
    // data-stars-default (the Simple hubs): the wall opens on that star band, All pill absent.
    this.stars = this.dataset.starsDefault || 'all';
    this.topic = 'all';
    this.product = 'all';
    this.source = this.sourceBar ? 'trustpilot' : 'all';
    // Keep the first-seen label: reconnection (theme-editor section moves) would
    // otherwise re-capture a label that already carries the "(N more)" suffix.
    if (this.moreBtn && !this.moreBtn.dataset.label) this.moreBtn.dataset.label = this.moreBtn.textContent.trim();
    [this.bar, this.sourceBar, this.topicBar, this.productBar].forEach((bar) => {
      if (!bar) return;
      bar.hidden = false; // no-JS leaves every bar hidden and every card visible
      bar.addEventListener('click', (e) => {
        const btn = e.target.closest('.dip-filter__btn');
        if (btn) this.#apply(bar, btn);
      });
    });
    this.moreBtn?.addEventListener('click', () => { this.extra += this.#baseCap(); this.#refresh(); });
    // Re-resolve whenever the grid's geometry changes. The cap depends on the viewport AND
    // on how many columns the grid actually renders, and neither is knowable at
    // custom-element upgrade: the viewport can still be unsized (which used to freeze
    // every wall at the mobile cap for the whole session, so desktop showed a part-filled
    // row) and a wall inside an unselected tab panel has no layout at all until its tab is
    // picked. A ResizeObserver on the grid covers all three: first layout, tab reveal and
    // window resize. Refreshing only when the column count actually changes keeps it from
    // feeding back on its own height changes.
    this.grid = this.querySelector('.dip-tstm__grid, .dip-htstm__grid, .dip-reviews__grid');
    // Three signals, one handler, because no single one covers every case: the media query
    // catches a breakpoint crossing, window resize catches a column change within a
    // breakpoint, and the ResizeObserver catches a wall gaining layout when its tab is
    // first selected (no window event fires for that). Re-refreshing only when the column
    // count or breakpoint actually changed keeps this off the resize hot path and stops the
    // observer feeding back on the height changes its own refresh causes.
    this.mq = matchMedia('(max-width: 699px)');
    // Debounced on a timer, NOT requestAnimationFrame: rAF is throttled to nothing in a
    // background tab, so a resize while the tab is hidden would leave the wall on a stale
    // cap until something else refreshed it. This is layout bookkeeping, not animation.
    this.onGeometry = () => {
      clearTimeout(this.geoTimer);
      this.geoTimer = setTimeout(() => {
        const cols = this.#columns();
        const narrow = this.mq.matches;
        if (cols === this.lastCols && narrow === this.lastNarrow) return;
        this.lastCols = cols;
        this.lastNarrow = narrow;
        this.#refresh();
      }, 100);
    };
    addEventListener('resize', this.onGeometry, { passive: true });
    this.mq.addEventListener('change', this.onGeometry);
    if (this.grid && 'ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.onGeometry);
      this.ro.observe(this.grid);
    }
    this.#refresh();
    // Full-pool hydration (2026-08-24): sections cap at 50 blocks, so the complete review pool
    // lives in a JSON asset (kept fresh by scripts/reviews-sync) and replaces the SSR fallback
    // cards once fetched. Any failure leaves the server-rendered 50 standing.
    if (this.dataset.reviewsSrc) {
      fetch(this.dataset.reviewsSrc, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => { if (data?.reviews?.length) this.#hydrate(data.reviews); })
        .catch(() => { /* asset missing or offline: the SSR cards stand */ });
    }
  }

  #hydrate(list) {
    const grid = this.querySelector('.dip-reviews__grid');
    if (!grid) return;
    grid.textContent = '';
    list.forEach((r) => grid.appendChild(this.#buildCard(r)));
    this.cards = [...grid.querySelectorAll('.dip-rw-card')];
    this.wallCards = this.cards;
    this.extra = 0;
    this.#refresh();
  }

  // One review card, byte-compatible with snippets/dip-review-wall.liquid's markup so every
  // style, clamp and provenance rule applies unchanged. All strings land via textContent.
  #buildCard(r) {
    const el = (tag, cls, text) => {
      const n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    };
    const src = r.source || 'trustpilot';
    const card = el('article', 'dip-rw-card');
    card.dataset.stars = String(r.rating || 5);
    card.dataset.source = src;
    if (r.topics) card.dataset.topics = r.topics;
    if (r.products) card.dataset.products = r.products;
    const stars = el('span', `dip-rw-stars${src !== 'trustpilot' ? ' dip-rw-stars--store' : ''}`);
    stars.setAttribute('aria-label', `Rated ${r.rating} out of 5`);
    for (let n = 1; n <= 5; n += 1) {
      const i = el('i', n > (r.rating || 5) ? 'empty' : '');
      // Same drawing + white fill as snippets/dip-star.liquid: the box behind it carries the
      // colour (Trustpilot green / empty grey), so the glyph itself is always white.
      i.innerHTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="#fff" aria-hidden="true"><path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.2l7.1-.6z"/></svg>';
      stars.appendChild(i);
    }
    card.appendChild(stars);
    if (r.title) card.appendChild(el('h3', 'dip-rw-title', r.title));
    card.appendChild(el('blockquote', 'dip-rw-quote', r.quote || ''));
    if (r.reply) {
      const rep = el('div', 'dip-rw-reply');
      rep.appendChild(el('span', 'dip-rw-reply-h', 'Reply from Dip'));
      rep.appendChild(el('p', '', r.reply));
      card.appendChild(rep);
    }
    const cite = el('cite', 'dip-rw-cite');
    const who = el('span', 'dip-rw-who');
    const initials = (r.name || '').trim().split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('').toUpperCase();
    const av = el('span', 'dip-rw-av', initials);
    av.setAttribute('aria-hidden', 'true');
    who.appendChild(av);
    const nameWrap = el('span', 'dip-rw-name');
    const b = el('b', '', r.name || '');
    nameWrap.appendChild(b);
    if (r.date) nameWrap.appendChild(el('span', '', r.date));
    who.appendChild(nameWrap);
    cite.appendChild(who);
    if (src === 'trustpilot') {
      const badge = el('span', 'dip-rw-src');
      badge.innerHTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="#00B67A" aria-hidden="true"><path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.2l7.1-.6z"/></svg>';
      badge.appendChild(document.createTextNode('Verified on Trustpilot'));
      cite.appendChild(badge);
    } else if (src === 'loox') {
      const badge = el('span', 'dip-rw-src dip-rw-src--store');
      badge.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor"/><path d="M8 12.2l2.6 2.6L16.4 9" stroke="#fff" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      badge.appendChild(document.createTextNode('Loox verified purchase'));
      cite.appendChild(badge);
    }
    card.appendChild(cite);
    return card;
  }

  disconnectedCallback() {
    this.ro?.disconnect();
    clearTimeout(this.geoTimer);
    if (this.onGeometry) {
      removeEventListener('resize', this.onGeometry);
      this.mq?.removeEventListener('change', this.onGeometry);
    }
  }

  // Rendered column count, or 0 while the grid has no layout (hidden tab panel), where
  // grid-template-columns reports its unresolved specified value rather than used pixels.
  #columns() {
    if (!this.grid || !this.grid.clientWidth) return 0;
    return getComputedStyle(this.grid).gridTemplateColumns.split(' ').filter(Boolean).length;
  }

  // Whole rows only (Charlie 2026-07-30): a part-filled last row reads as missing content.
  // Round the configured count UP to a multiple of the column count, then, if the wall
  // does not hold enough cards to finish that last row, drop back to the whole rows it can
  // fill and let Show-more reveal the remainder. Always leaves at least one row.
  #rowFilledCap(base, total) {
    const cols = this.#columns();
    if (cols < 2) return base; // single column, or not laid out yet: every count fills its row
    const snapped = Math.ceil(base / cols) * cols;
    const shown = Math.min(snapped, total);
    if (shown % cols === 0) return snapped;
    return Math.max(cols, Math.floor(shown / cols) * cols);
  }

  #matches(card) {
    if (this.source !== 'all' && (card.dataset.source || 'trustpilot') !== this.source) return false;
    if (this.stars !== 'all' && card.dataset.stars !== this.stars) return false;
    if (this.topic !== 'all') {
      const topics = card.dataset.topics
        ? card.dataset.topics.split(',').map((t) => t.trim())
        : (card.dataset.topic ? [card.dataset.topic] : []);
      if (!topics.includes(this.topic)) return false;
    }
    if (this.product !== 'all') {
      const products = card.dataset.products
        ? card.dataset.products.split(',').map((t) => t.trim())
        : [];
      if (!products.includes(this.product)) return false;
    }
    return true;
  }

  #baseCap() {
    return matchMedia('(max-width: 699px)').matches
      ? Number(this.dataset.capMobile) || 4
      : Number(this.dataset.capDesktop) || 6;
  }

  #refresh(label) {
    // Resolve the cap per refresh (not at connect): the viewport can be unsized at
    // custom-element upgrade, and this also tracks orientation changes for free.
    // Show-more PAGES (2026-08-24): each click adds another base-cap's worth of rows rather
    // than opening the whole pool, and the button never states a remainder count.
    const baseCap = this.#baseCap() + (this.extra || 0);
    let matched = 0;
    this.cards.forEach((card) => { const show = this.#matches(card); card.hidden = !show; if (show) matched += 1; });
    // Cap resolved against the cards this view actually holds, so a short filter/tab still
    // shows whole rows rather than a ragged one.
    this.cap = this.#rowFilledCap(baseCap, this.wallCards.filter((c) => this.#matches(c)).length);
    // Cap the wall cards of the current view (non-wall cards, e.g. video testimonials, never cap).
    let capped = 0;
    {
      let i = 0;
      this.wallCards.forEach((card) => {
        if (card.hidden) return;
        if (i >= this.cap) { card.hidden = true; capped += 1; }
        i += 1;
      });
    }
    if (this.moreBtn) {
      this.moreBtn.textContent = this.moreBtn.dataset.label;
      this.moreBtn.hidden = capped === 0;
    }
    if (this.status) {
      const visible = matched - capped;
      const unfiltered = this.stars === 'all' && this.topic === 'all' && this.source === 'all' && this.product === 'all';
      this.status.textContent = unfiltered
        ? `Showing ${visible} of ${matched} reviews`
        : `Showing ${visible} of ${matched} review${matched === 1 ? '' : 's'}${label ? ` for ${label}` : ''}`;
    }
  }

  #apply(bar, active) {
    bar.querySelectorAll('.dip-filter__btn').forEach((b) => b.setAttribute('aria-pressed', b === active ? 'true' : 'false'));
    if ('source' in active.dataset) this.source = active.dataset.source;
    else if ('stars' in active.dataset) this.stars = active.dataset.stars;
    else if ('product' in active.dataset) this.product = active.dataset.product || 'all';
    else this.topic = active.dataset.topic || 'all';
    this.extra = 0;
    this.#refresh(active.textContent.trim());
  }
}

// ---- Video testimonial card ------------------------------------------------
// Server renders the <video controls preload="none" poster> visible (so no-JS can
// play it) plus a poster-button marked hidden. JS flips that: button shown, video
// hidden until play — nicer resting state, and lets us pause the other cards.
const playing = new Set();
class DipVideoCard extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.btn = this.querySelector('.dip-vt__play');
    if (!this.video || !this.btn) return;
    this.btn.hidden = false;
    this.video.hidden = true;
    this.btn.addEventListener('click', () => this.#play());
    this.video.addEventListener('pause', () => playing.delete(this.video));
  }

  #play() {
    playing.forEach((v) => { if (v !== this.video) v.pause(); });
    this.btn.hidden = true;
    this.video.hidden = false;
    const p = this.video.play();
    if (p?.catch) p.catch(() => {}); // ignore interrupted-play rejections
    playing.add(this.video);
  }
}

// ---- Compact review quotes (Read more) --------------------------------------
// Site-wide rule (Charlie 2026-07-30): every review quote renders compact at the
// same 4-line cap, with a Read more / Show less toggle only where the quote
// actually overflows. Progressive: the clamp class is added here, so no-JS shows
// full quotes. Measurement happens on first intersection (not at connect) because
// quotes inside hidden tab panels / capped walls have zero layout until shown.
function setUpQuoteClamp(q) {
  q.classList.add('dip-rw-clamp');
  if (q.scrollHeight - q.clientHeight < 2) { q.classList.remove('dip-rw-clamp'); return; }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'dip-rw-more';
  btn.textContent = 'Read more';
  btn.setAttribute('aria-expanded', 'false');
  btn.addEventListener('click', () => {
    const open = q.classList.toggle('is-open');
    btn.textContent = open ? 'Show less' : 'Read more';
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
  q.insertAdjacentElement('afterend', btn);
}

function initQuoteClamps() {
  const quotes = document.querySelectorAll('.dip-rw-quote, .dip-quotes .dip-quote > p');
  if (!quotes.length) return;
  if (!('IntersectionObserver' in window)) { quotes.forEach(setUpQuoteClamp); return; }
  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      setUpQuoteClamp(e.target);
      obs.unobserve(e.target);
    });
  }, { rootMargin: '120px 0px' });
  quotes.forEach((q) => io.observe(q));
}

// ---- Count-up --------------------------------------------------------------
// The server-rendered text IS the true value ("100%", "4.1", "2,098"): reduced
// motion and no-JS need nothing. Otherwise animate 0 -> value once on view.
function countUp(el) {
  const raw = el.textContent.trim();
  const m = raw.match(/^([^0-9]*)([0-9][0-9.,]*)(.*)$/);
  if (!m) return;
  const [, prefix, numText, suffix] = m;
  const decimals = (numText.split('.')[1] || '').length;
  const target = parseFloat(numText.replace(/,/g, ''));
  if (!isFinite(target)) return;
  const grouped = numText.includes(',');
  const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grouped,
  });
  const t0 = performance.now();
  const DUR = 900;
  const tick = (t) => {
    // Clamp at 0: rAF frame timestamps can precede the t0 captured when
    // scheduling, and a negative p eases below zero ("-0" on the first frame).
    const p = Math.min(1, Math.max(0, (t - t0) / DUR));
    const eased = 1 - Math.pow(1 - p, 3);
    el.textContent = prefix + fmt.format(target * eased) + suffix;
    if (p < 1) requestAnimationFrame(tick);
    else el.textContent = raw; // land exactly on the server-rendered value
  };
  requestAnimationFrame(tick);
}

function initCountUps() {
  const els = document.querySelectorAll('[data-countup]');
  if (!els.length || REDUCED.matches || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => {
      if (e.isIntersecting) { countUp(e.target); obs.unobserve(e.target); }
    });
  }, { threshold: 0.4 });
  els.forEach((el) => io.observe(el));
}

if (!customElements.get('dip-review-wall')) customElements.define('dip-review-wall', DipReviewWall);
if (!customElements.get('dip-video-card')) customElements.define('dip-video-card', DipVideoCard);
function initBelowFold() {
  initCountUps();
  initQuoteClamps();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBelowFold, { once: true });
else initBelowFold();

// ---- Reveal-on-scroll (same additive entrance as dip-buy-box.js) --------------
// The same block lives in dip-buy-box.js and dip-home.js so any page animates whichever
// of the three it loads; the flag makes the first one to run the only one that binds.
// A page built only from below-fold sections (reviews, tips, ingredients) loads neither
// of the other two, which is why this copy exists.
(() => {
  if (window.__dipRevealInit) return;
  window.__dipRevealInit = true;
  const reveal = () => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries, obs) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); obs.unobserve(e.target); } }),
      { rootMargin: '0px 0px -4% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.dip-reveal').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!(r.top < window.innerHeight && r.bottom > 0)) { el.classList.add('dip-pre'); io.observe(el); }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reveal, { once: true });
  else reveal();
})();

// ---- Reviews wall: per-product band colour (Charlie 2026-08-26) --------------
// On a grouped wall each Product tab may carry data-bg (its product's colour);
// selecting it repaints the whole section, so the band follows the product.
// <dip-tabs> keeps handling the actual tab mechanics; this only paints.
(() => {
  const init = () => {
    document.querySelectorAll('.dip-reviews-section').forEach((sec) => {
      sec.querySelectorAll('.dip-filter__btn[data-bg]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.dataset.bg) sec.style.background = btn.dataset.bg;
        });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
